/**
 * AP Approval Inbox — lists AP Bills and AP Payments with workflow status
 * Submitted or PendingApproval so approvers can act without opening each record.
 *
 * Permission guards:
 *   AP_BILL_APPROVE / AP_BILL_REJECT   — shown per entity type.
 *   AP_PAYMENT_APPROVE / AP_PAYMENT_REJECT — shown per entity type.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CheckCircle2, XCircle, Clock, RefreshCw, FileText,
  ChevronLeft, ChevronRight, Inbox, CreditCard,
} from 'lucide-react';

import { apBillApi }    from '@/api/apBill.api';
import { apPaymentApi } from '@/api/apPayment.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';
import type { RootState } from '@/state/store';

import type { ApBillListDto }    from '@/types/apBill.types';
import type { ApPaymentListDto } from '@/types/apPayment.types';
import {
  AP_WORKFLOW_STATUS_LABELS,
  AP_WORKFLOW_STATUS_COLORS,
} from '@/types/apBill.types';

// ── Formatters ────────────────────────────────────────────────────────────────

const fmt    = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtDate = (s?: string | null) => (s ? fmt.format(new Date(s)) : '—');
const fmtAmt  = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Tabs ──────────────────────────────────────────────────────────────────────

type ActiveTab = 'bills' | 'payments';

// ── Main Component ────────────────────────────────────────────────────────────

export const ApApprovalInboxPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canApproveBill    = hasPermission(Permissions.AP_BILL_APPROVE);
  const canRejectBill     = hasPermission(Permissions.AP_BILL_REJECT);
  const canApprovePayment = hasPermission(Permissions.AP_PAYMENT_APPROVE);
  const canRejectPayment  = hasPermission(Permissions.AP_PAYMENT_REJECT);

  const session = useSelector((s: RootState) => s.session.session);

  const [activeTab, setActiveTab] = useState<ActiveTab>('bills');

  // ── Bills state ───────────────────────────────────────────────────────────
  const [bills,       setBills]       = useState<ApBillListDto[]>([]);
  const [billLoading, setBillLoading] = useState(false);
  const [billTotal,   setBillTotal]   = useState(0);
  const [billPage,    setBillPage]    = useState(1);

  // ── Payments state ────────────────────────────────────────────────────────
  const [payments,    setPayments]    = useState<ApPaymentListDto[]>([]);
  const [pymtLoading, setPymtLoading] = useState(false);
  const [pymtTotal,   setPymtTotal]   = useState(0);
  const [pymtPage,    setPymtPage]    = useState(1);

  const pageSize = 20;

  // ── Load bills ────────────────────────────────────────────────────────────
  const loadBills = useCallback(async (p: number) => {
    setBillLoading(true);
    try {
      const base = {
        pageNumber:     p,
        pageSize,
        sortBy:         'billDate',
        sortDescending: true,
        locationId:     session?.locationId   ?? undefined,
        costCenterId:   session?.costCenterId ?? undefined,
      };
      const [r1, r2] = await Promise.all([
        apBillApi.getList({ ...base, workflowStatus: 'Submitted' }),
        apBillApi.getList({ ...base, workflowStatus: 'PendingApproval' }),
      ]);
      const merged = [
        ...(r1.data?.data?.items ?? []),
        ...(r2.data?.data?.items ?? []),
      ].sort((a, b) => new Date(b.billDate).getTime() - new Date(a.billDate).getTime());
      setBills(merged);
      setBillTotal((r1.data?.data?.totalCount ?? 0) + (r2.data?.data?.totalCount ?? 0));
    } catch (err) {
      console.error('Failed to load AP bill inbox', err);
    } finally {
      setBillLoading(false);
    }
  }, [session]);

  // ── Load payments ─────────────────────────────────────────────────────────
  const loadPayments = useCallback(async (p: number) => {
    setPymtLoading(true);
    try {
      const base = {
        pageNumber:     p,
        pageSize,
        sortBy:         'paymentDate',
        sortDescending: true,
        locationId:     session?.locationId   ?? undefined,
        costCenterId:   session?.costCenterId ?? undefined,
      };
      const [r1, r2] = await Promise.all([
        apPaymentApi.getList({ ...base, workflowStatus: 'Submitted' }),
        apPaymentApi.getList({ ...base, workflowStatus: 'PendingApproval' }),
      ]);
      const merged = [
        ...(r1.data?.data?.items ?? []),
        ...(r2.data?.data?.items ?? []),
      ].sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
      setPayments(merged);
      setPymtTotal((r1.data?.data?.totalCount ?? 0) + (r2.data?.data?.totalCount ?? 0));
    } catch (err) {
      console.error('Failed to load AP payment inbox', err);
    } finally {
      setPymtLoading(false);
    }
  }, [session]);

  useEffect(() => { loadBills(billPage);    }, [billPage,    loadBills]);
  useEffect(() => { loadPayments(pymtPage); }, [pymtPage, loadPayments]);

  const billTotalPages = Math.ceil(billTotal / pageSize);
  const pymtTotalPages = Math.ceil(pymtTotal / pageSize);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Inbox size={20} className="text-orange-600" />
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                AP Approval Inbox
              </h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Bills and payments awaiting your approval action
            </p>
          </div>
          <button
            onClick={() => { loadBills(billPage); loadPayments(pymtPage); }}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Summary count banner */}
        {(billTotal > 0 || pymtTotal > 0) && (
          <div className="mt-3 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-sm text-orange-700 dark:text-orange-400">
            {billTotal} Bill{billTotal !== 1 ? 's' : ''} | {pymtTotal} Payment{pymtTotal !== 1 ? 's' : ''} pending your approval
          </div>
        )}

        {/* Permission notice */}
        {!canApproveBill && !canRejectBill && !canApprovePayment && !canRejectPayment && (
          <div className="mt-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
            You don't currently have permission to approve or reject AP documents.
            Contact your administrator if this is incorrect.
          </div>
        )}

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <div className="flex gap-1 mt-4">
          <button
            onClick={() => setActiveTab('bills')}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
              activeTab === 'bills'
                ? 'border-orange-600 text-orange-600 bg-orange-50 dark:bg-orange-900/20'
                : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300',
            )}
          >
            <FileText size={14} />
            Bills
            {billTotal > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-orange-600 text-white text-[10px] font-bold rounded-full">
                {billTotal}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
              activeTab === 'payments'
                ? 'border-orange-600 text-orange-600 bg-orange-50 dark:bg-orange-900/20'
                : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300',
            )}
          >
            <CreditCard size={14} />
            Payments
            {pymtTotal > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-orange-600 text-white text-[10px] font-bold rounded-full">
                {pymtTotal}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Bills Tab ───────────────────────────────────────────────────────── */}
      {activeTab === 'bills' && (
        <>
          <div className="flex-1 overflow-auto">
            {billLoading ? (
              <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
            ) : bills.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <CheckCircle2 size={40} className="mb-3 opacity-30 text-green-500" />
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  No bills awaiting approval
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Submitted bills will appear here.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bill #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Supplier Inv #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bill Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Supplier</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">CCY</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {bills.map(bill => (
                    <tr key={bill.id} className="hover:bg-orange-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td
                        className="px-4 py-3 font-mono font-semibold text-orange-700 dark:text-orange-400 cursor-pointer hover:underline"
                        onClick={() => navigate(AppRoutes.AP_BILL_DETAIL.replace(':id', bill.id))}
                      >
                        {bill.billNumber}
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                        {bill.supplierInvoiceNumber ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-sm">
                        {fmtDate(bill.billDate)}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-sm">
                        {fmtDate(bill.dueDate)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">{bill.supplierName}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{bill.supplierCode}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900 dark:text-white">
                        {fmtAmt(bill.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                        {bill.currencyCode}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <Clock size={13} className="text-orange-500 shrink-0" />
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            AP_WORKFLOW_STATUS_COLORS[bill.workflowStatus],
                          )}>
                            {AP_WORKFLOW_STATUS_LABELS[bill.workflowStatus]}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(AppRoutes.AP_BILL_DETAIL.replace(':id', bill.id))}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                       bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                                       hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            title="Open bill to review"
                          >
                            <FileText size={11} /> Review
                          </button>
                          {canApproveBill && (
                            <button
                              onClick={() => navigate(AppRoutes.AP_BILL_DETAIL.replace(':id', bill.id))}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-green-600 text-white hover:bg-green-700 transition-colors"
                              title="Open to approve"
                            >
                              <CheckCircle2 size={11} /> Approve
                            </button>
                          )}
                          {canRejectBill && (
                            <button
                              onClick={() => navigate(AppRoutes.AP_BILL_DETAIL.replace(':id', bill.id))}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-red-600 text-white hover:bg-red-700 transition-colors"
                              title="Open to reject"
                            >
                              <XCircle size={11} /> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Bills pagination */}
          {billTotalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
              <p className="text-sm text-slate-500 dark:text-gray-400">
                Page {billPage} of {billTotalPages} · {billTotal.toLocaleString()} records
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setBillPage(p => p - 1)} disabled={billPage <= 1}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(5, billTotalPages) }, (_, i) => {
                  const n = Math.max(1, Math.min(billPage - 2, billTotalPages - 4)) + i;
                  return (
                    <button key={n} onClick={() => setBillPage(n)}
                      className={cn('w-8 h-8 text-sm rounded transition-colors',
                        n === billPage
                          ? 'bg-orange-600 text-white font-semibold'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300')}>
                      {n}
                    </button>
                  );
                })}
                <button onClick={() => setBillPage(p => p + 1)} disabled={billPage >= billTotalPages}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Payments Tab ────────────────────────────────────────────────────── */}
      {activeTab === 'payments' && (
        <>
          <div className="flex-1 overflow-auto">
            {pymtLoading ? (
              <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
            ) : payments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <CheckCircle2 size={40} className="mb-3 opacity-30 text-green-500" />
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  No payments awaiting approval
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Submitted payments will appear here.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Payment #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Payment Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Method</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">CCY</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {payments.map(pmt => (
                    <tr key={pmt.id} className="hover:bg-orange-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td
                        className="px-4 py-3 font-mono font-semibold text-orange-700 dark:text-orange-400 cursor-pointer hover:underline"
                        onClick={() => navigate(AppRoutes.AP_PAYMENT_DETAIL.replace(':id', pmt.id))}
                      >
                        {pmt.paymentNumber}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-sm">
                        {fmtDate(pmt.paymentDate)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">{pmt.supplierName}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{pmt.supplierCode}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                        {pmt.paymentMethod}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900 dark:text-white">
                        {fmtAmt(pmt.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                        {pmt.currencyCode}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <Clock size={13} className="text-orange-500 shrink-0" />
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            AP_WORKFLOW_STATUS_COLORS[pmt.workflowStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
                          )}>
                            {AP_WORKFLOW_STATUS_LABELS[pmt.workflowStatus] ?? pmt.workflowStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(AppRoutes.AP_PAYMENT_DETAIL.replace(':id', pmt.id))}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                       bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                                       hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            title="Open payment to review"
                          >
                            <FileText size={11} /> Review
                          </button>
                          {canApprovePayment && (
                            <button
                              onClick={() => navigate(AppRoutes.AP_PAYMENT_DETAIL.replace(':id', pmt.id))}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-green-600 text-white hover:bg-green-700 transition-colors"
                              title="Open to approve"
                            >
                              <CheckCircle2 size={11} /> Approve
                            </button>
                          )}
                          {canRejectPayment && (
                            <button
                              onClick={() => navigate(AppRoutes.AP_PAYMENT_DETAIL.replace(':id', pmt.id))}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-red-600 text-white hover:bg-red-700 transition-colors"
                              title="Open to reject"
                            >
                              <XCircle size={11} /> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Payments pagination */}
          {pymtTotalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
              <p className="text-sm text-slate-500 dark:text-gray-400">
                Page {pymtPage} of {pymtTotalPages} · {pymtTotal.toLocaleString()} records
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setPymtPage(p => p - 1)} disabled={pymtPage <= 1}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(5, pymtTotalPages) }, (_, i) => {
                  const n = Math.max(1, Math.min(pymtPage - 2, pymtTotalPages - 4)) + i;
                  return (
                    <button key={n} onClick={() => setPymtPage(n)}
                      className={cn('w-8 h-8 text-sm rounded transition-colors',
                        n === pymtPage
                          ? 'bg-orange-600 text-white font-semibold'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300')}>
                      {n}
                    </button>
                  );
                })}
                <button onClick={() => setPymtPage(p => p + 1)} disabled={pymtPage >= pymtTotalPages}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
